/**
 * Hook for tracking relational exercise completions and achievements
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ExerciseCompletion {
  id: string;
  exercise_id: string;
  exercise_type: 'self-connection' | 'relationship';
  relationship_type?: string;
  responses: Record<string, string>;
  completed_at: string;
  duration_seconds?: number;
}

interface Achievement {
  id: string;
  achievement_type: string;
  achievement_name: string;
  description: string;
  points: number;
  earned_at: string;
}

const ACHIEVEMENT_DEFINITIONS = {
  first_self_connection: {
    name: 'Inner Explorer',
    description: 'Completed your first self-connection exercise',
    points: 50,
  },
  self_connection_5: {
    name: 'Self-Aware',
    description: 'Completed 5 self-connection exercises',
    points: 100,
  },
  self_connection_10: {
    name: 'Deeply Connected',
    description: 'Completed 10 self-connection exercises',
    points: 200,
  },
  first_relationship: {
    name: 'Relationship Builder',
    description: 'Completed your first relationship exercise',
    points: 50,
  },
  relationship_5: {
    name: 'Communication Champion',
    description: 'Completed 5 relationship exercises',
    points: 100,
  },
  relationship_10: {
    name: 'Connection Master',
    description: 'Completed 10 relationship exercises',
    points: 200,
  },
  weekly_streak: {
    name: 'Consistent Connector',
    description: 'Completed exercises for 7 days in a row',
    points: 150,
  },
  all_categories: {
    name: 'Well-Rounded',
    description: 'Completed exercises in all self-connection categories',
    points: 250,
  },
};

export function useRelationalProgress() {
  const [completions, setCompletions] = useState<ExerciseCompletion[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalPoints, setTotalPoints] = useState(0);

  // Fetch user's completions and achievements
  useEffect(() => {
    const fetchProgress = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsLoading(false);
        return;
      }

      const [completionsResult, achievementsResult] = await Promise.all([
        supabase
          .from('relational_exercise_completions')
          .select('*')
          .eq('user_id', user.id)
          .order('completed_at', { ascending: false }),
        supabase
          .from('relational_achievements')
          .select('*')
          .eq('user_id', user.id)
          .order('earned_at', { ascending: false }),
      ]);

      if (completionsResult.data) {
        setCompletions(completionsResult.data as ExerciseCompletion[]);
      }

      if (achievementsResult.data) {
        setAchievements(achievementsResult.data as Achievement[]);
        const points = achievementsResult.data.reduce((sum, a) => sum + (a.points || 0), 0);
        setTotalPoints(points);
      }

      setIsLoading(false);
    };

    fetchProgress();
  }, []);

  // Complete an exercise
  const completeExercise = useCallback(async (
    exerciseId: string,
    exerciseType: 'self-connection' | 'relationship',
    responses: Record<string, string>,
    relationshipType?: string,
    durationSeconds?: number
  ) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error('Please sign in to save your progress');
      return false;
    }

    // Save the completion
    const { data, error } = await supabase
      .from('relational_exercise_completions')
      .insert({
        user_id: user.id,
        exercise_id: exerciseId,
        exercise_type: exerciseType,
        relationship_type: relationshipType,
        responses,
        duration_seconds: durationSeconds,
      })
      .select()
      .single();

    if (error) {
      console.error('Error saving exercise completion:', error);
      toast.error('Could not save your progress');
      return false;
    }

    // Update local state
    setCompletions(prev => [data as ExerciseCompletion, ...prev]);

    // Check for achievements
    await checkAndAwardAchievements(user.id, exerciseType);

    toast.success('Exercise completed!');
    return true;
  }, []);

  // Check and award achievements
  const checkAndAwardAchievements = async (userId: string, exerciseType: string) => {
    // Get current counts
    const { data: completionCounts } = await supabase
      .from('relational_exercise_completions')
      .select('exercise_type')
      .eq('user_id', userId);

    if (!completionCounts) return;

    const selfConnectionCount = completionCounts.filter(c => c.exercise_type === 'self-connection').length;
    const relationshipCount = completionCounts.filter(c => c.exercise_type === 'relationship').length;

    // Get existing achievements
    const { data: existingAchievements } = await supabase
      .from('relational_achievements')
      .select('achievement_type')
      .eq('user_id', userId);

    const earnedTypes = new Set(existingAchievements?.map(a => a.achievement_type) || []);

    // Check which achievements to award
    const achievementsToAward: string[] = [];

    if (exerciseType === 'self-connection') {
      if (selfConnectionCount === 1 && !earnedTypes.has('first_self_connection')) {
        achievementsToAward.push('first_self_connection');
      }
      if (selfConnectionCount >= 5 && !earnedTypes.has('self_connection_5')) {
        achievementsToAward.push('self_connection_5');
      }
      if (selfConnectionCount >= 10 && !earnedTypes.has('self_connection_10')) {
        achievementsToAward.push('self_connection_10');
      }
    }

    if (exerciseType === 'relationship') {
      if (relationshipCount === 1 && !earnedTypes.has('first_relationship')) {
        achievementsToAward.push('first_relationship');
      }
      if (relationshipCount >= 5 && !earnedTypes.has('relationship_5')) {
        achievementsToAward.push('relationship_5');
      }
      if (relationshipCount >= 10 && !earnedTypes.has('relationship_10')) {
        achievementsToAward.push('relationship_10');
      }
    }

    // Award achievements
    for (const achievementType of achievementsToAward) {
      const def = ACHIEVEMENT_DEFINITIONS[achievementType as keyof typeof ACHIEVEMENT_DEFINITIONS];
      if (!def) continue;

      const { data: newAchievement } = await supabase
        .from('relational_achievements')
        .insert({
          user_id: userId,
          achievement_type: achievementType,
          achievement_name: def.name,
          description: def.description,
          points: def.points,
        })
        .select()
        .single();

      if (newAchievement) {
        setAchievements(prev => [newAchievement as Achievement, ...prev]);
        setTotalPoints(prev => prev + def.points);
        toast.success(`Achievement Unlocked: ${def.name}!`, {
          description: def.description,
        });
      }
    }
  };

  // Get completion status for an exercise
  const isExerciseCompleted = useCallback((exerciseId: string) => {
    return completions.some(c => c.exercise_id === exerciseId);
  }, [completions]);

  // Get completed exercise IDs
  const getCompletedExerciseIds = useCallback(() => {
    return new Set(completions.map(c => c.exercise_id));
  }, [completions]);

  // Get stats
  const getStats = useCallback(() => {
    const selfConnectionCount = completions.filter(c => c.exercise_type === 'self-connection').length;
    const relationshipCount = completions.filter(c => c.exercise_type === 'relationship').length;
    
    return {
      totalCompletions: completions.length,
      selfConnectionCount,
      relationshipCount,
      totalPoints,
      achievementCount: achievements.length,
    };
  }, [completions, achievements, totalPoints]);

  return {
    completions,
    achievements,
    isLoading,
    totalPoints,
    completeExercise,
    isExerciseCompleted,
    getCompletedExerciseIds,
    getStats,
  };
}
